import { Strings } from '@/lib/constants/strings'
import { AppConstants } from '../../lib/constants/constants'
import { TransactionStatusCdLabel } from '../../lib/enums/bi-enums'
import { ProspectiveAgentData } from '../service/models/prospectiveagentprofile'
import { EngagementTabDisplayCdEnum, StageCdEnum } from '../service/models/expApiTypes'
import { RecruitingActivityResponse } from '../service/models/prospectiveagentactivity'

/**
 * Returns the valid string representation of a value, or a fallback if the value is undefined, null, or empty.
 *
 * @param value - The value to validate and convert to string.
 * @param fallback - The fallback string to use if the value is invalid. Defaults to '-'.
 * @returns The valid string representation of the value or the fallback.
 */
export const getValidValue = (value: unknown, fallback: string = '-'): string => {
	if (value === undefined || value === null || value === '') {
		return fallback
	}

	// Handle different types explicitly
	switch (typeof value) {
		case 'string':
		case 'number':
		case 'boolean':
			return String(value)

		case 'object':
			try {
				return JSON.stringify(value)
			} catch {
				return fallback
			}

		case 'function':
			return value.toString()

		case 'symbol':
			return value.toString()

		case 'bigint':
			return value.toString()

		default:
			return fallback
	}
}

/**
 * Get status values based on selection
 * @param statusArray - Array of status items (ON_MARKET_STATUS_ITEMS or OFF_MARKET_STATUS_ITEMS)
 * @param selectedValue - Selected status value ('all', 'Active', 'Pending', etc.)
 * @returns Array of status values
 */
export const getStatusValues = (
	statusArray: Array<{ label: string; value: string; disabled: boolean }>,
	selectedValue: string
): string[] => {
	if (selectedValue === 'All') {
		return statusArray.map(item => item.value)
	}

	const selectedLabels = selectedValue.split(',').map(label => label.trim().toLowerCase())
	const foundItems = statusArray.filter(item => selectedLabels.includes(item.label.toLowerCase()))

	return foundItems.length > 0 ? foundItems.map(item => item.value) : []
}

/**
 * Returns the label corresponding to a given status value.
 *
 * This function searches through the `ON_MARKET_STATUS_ITEMS` and `OFF_MARKET_STATUS_ITEMS`
 * arrays (from `AppConstants`) to find an item whose `value` matches the provided
 * `status` string. If a matching item is found, its `label` is returned.
 * If no match is found, the original `status` string is returned as a fallback.
 *
 * @param status - The status value to look up.
 * @returns The label associated with the status, or the status itself if not found.
 */
export const getStatusLabel = (status: string): string => {
	const { ON_MARKET_STATUS_ITEMS, OFF_MARKET_STATUS_ITEMS, ON_MARKET_STATUS_ITEMS_TRANSACTION } = AppConstants
	const allStatusItems = [
		...ON_MARKET_STATUS_ITEMS,
		...OFF_MARKET_STATUS_ITEMS,
		...(Array.isArray(ON_MARKET_STATUS_ITEMS_TRANSACTION)
			? ON_MARKET_STATUS_ITEMS_TRANSACTION
			: [ON_MARKET_STATUS_ITEMS_TRANSACTION]),
	]
	return allStatusItems.find(item => item.value === status)?.label ?? status
}

/**
 * Generates a chart title based on checkbox selections and graph data type.
 *
 * @param checkBoxValues - Object with added and pending boolean flags
 * @param graphDataType - Either count or volume data type
 * @returns Formatted chart title string with capitalized first letter
 *
 * @example
 *
 * getChartTitle({ added: true, pending: true }, 'count')
 * // Returns: "Added and pending listings by count"
 *
 * getChartTitle({ added: false, pending: false }, 'volume')
 * // Returns: "Listings by volume"
 *
 */
export const getChartTitle = (
	checkBoxValues: { added: boolean; pending: boolean },
	graphDataType: typeof Strings.LISTING_STATUS_SHOW_BY_COUNT_VALUE | typeof Strings.LISTING_STATUS_SHOW_BY_VOLUME_VALUE
): string => {
	const { added, pending } = checkBoxValues

	if (added && pending) {
		return Strings.LISTING_BREAKDOWN_CHART_TITLE(
			`${Strings.LISTING_BREAKDOWN_ADDED} and ${Strings.LISTING_BREAKDOWN_PENDING_LOWER}`,
			graphDataType
		)
	}
	if (added) {
		return Strings.LISTING_BREAKDOWN_CHART_TITLE(Strings.LISTING_BREAKDOWN_ADDED, graphDataType)
	}

	if (pending) {
		return Strings.LISTING_BREAKDOWN_CHART_TITLE(Strings.LISTING_BREAKDOWN_PENDING, graphDataType)
	}
	// Fallback (should never reach here due to conditions above)
	return `Listings by ${graphDataType}`
}
export const getTransactionStatusLabel = (status: string): string => {
	return TransactionStatusCdLabel[status as keyof typeof TransactionStatusCdLabel] ?? status
}
/**
 * Utility method to convert recruiter object format from label to title
 * @param obj - Object with label and value properties
 * @returns Object with title and value properties
 * @example
 * // Input: { label: "Select recruiter", value: "Select recruiter" }
 * // Output: { title: "Select recruiter", value: "Select recruiter" }
 */
export const convertLabelToTitle = (obj: { label: string; value: string }) => {
	return {
		title: obj.label,
		value: obj.value,
	}
}

/**
 * Utility method to convert an array of recruiter objects from label to title format
 * @param options - Array of objects with label and value properties
 * @returns Array of objects with title and value properties
 */
export const convertRecruiterOptionsFormat = (options: { label: string; value: string }[]) => {
	return options.map(convertLabelToTitle)
}
/**
 * Utility method to create selected recruiter data from comma-separated values
 * @param recruiterFilterValue - Comma-separated string of recruiter values
 * @param recruiterFilters - Array of available recruiter options
 * @returns Array of objects with title and value properties matching selected recruiters
 * @example
 * // Input: "John Doe,Jane Smith", [{ label: "John Doe", value: "john.doe" }, { label: "Jane Smith", value: "jane.smith" }]
 * // Output: [{ title: "John Doe", value: "john.doe" }, { title: "Jane Smith", value: "jane.smith" }]
 */
export const createSelectedRecruiterData = (
	recruiterFilterValue: string,
	recruiterFilters?: { label: string; value: string }[]
) => {
	if (!recruiterFilterValue || !recruiterFilters) {
		return []
	}

	// Split the comma-separated string and trim whitespace
	const selectedValues = recruiterFilterValue
		.split(',')
		.map(value => value.trim())
		.filter(value => value.length > 0)

	// Find matching recruiters and convert to the required format
	return selectedValues
		.map(selectedValue => {
			const matchedRecruiter = recruiterFilters.find(
				recruiter => recruiter.value === selectedValue || recruiter.label === selectedValue
			)
			return matchedRecruiter ? convertLabelToTitle(matchedRecruiter) : null
		})
		.filter(Boolean) as { title: string; value: string }[]
}

/**
 * Determines the display mode for the engagement tab based on agent profile and activity history.
 *
 * @param agentFullProfile - The prospective agent's full profile data including pipeline stage and agent status.
 * @param engegeMentHistory - The recruiting activity history for the agent.
 * @returns An engagement tab display mode: 'SHOW' (fully visible), 'PART' (partially visible), or 'HIDE' (hidden).
 *
 * @example
 * // An agent who is not in any recruitment pipeline stage or is in nurture or pipeline stages gives different result
 * // Result: Returns 'HIDE' - no recruitment context to display
 * const profile = { pipeline: null, agent: { currentAgentFl: false } };
 * const history = null;
 * displayEngagementTab(profile, history); // 'HIDE', 'SHOW' or 'PART' depending on history
 */
export const displayEngagementTab = (
	agentFullProfile: ProspectiveAgentData | null,
	engegeMentHistory: RecruitingActivityResponse | null
): string => {
	const isInNurtureOrPipeline = [
		StageCdEnum.NURT,
		StageCdEnum.IDEN,
		StageCdEnum.MEET,
		StageCdEnum.CNCT,
		StageCdEnum.NEGO,
		StageCdEnum.CONV,
	].includes(agentFullProfile?.pipeline?.pipelineStageCd as StageCdEnum)
	const notInPipeline = !agentFullProfile?.pipeline
	const isYourAgent = agentFullProfile?.agent?.currentAgentFl
	const checkIfActivitiesExist =
		engegeMentHistory?.activities.some(
			activity => activity.activityGroupCd === 'LOG' || activity.activityGroupCd === 'NOTE'
		) ?? false
	if (isInNurtureOrPipeline && !isYourAgent) {
		return EngagementTabDisplayCdEnum.SHOW
	}

	if (notInPipeline) {
		return EngagementTabDisplayCdEnum.HIDE
	}

	if (isYourAgent && checkIfActivitiesExist) {
		return EngagementTabDisplayCdEnum.PART
	}

	return checkIfActivitiesExist ? EngagementTabDisplayCdEnum.PART : EngagementTabDisplayCdEnum.HIDE
}

/**
 * Common sorter builder:
 * - supports primary + optional secondary key extractors
 * - supports alpha or numeric comparisons per key
 * - ignores punctuation, case-insensitive
 */
type SortKeyType = 'alpha' | 'numeric'

function normalizeSortText(input: string): string {
	return (input ?? '')
		.toLowerCase()
		.replace(/[^a-z0-9\s]+/gi, ' ') // ignore punctuation/special chars
		.replace(/\s+/g, ' ')
		.trim()
}

function compareAlpha(a: string, b: string): number {
	return normalizeSortText(a).localeCompare(normalizeSortText(b), undefined, { sensitivity: 'base' })
}

function toNumberOrNaN(input: string): number {
	const n = Number(String(input ?? '').trim())
	return Number.isFinite(n) ? n : Number.NaN
}

function compareNumeric(a: string, b: string): number {
	const an = toNumberOrNaN(a)
	const bn = toNumberOrNaN(b)
	// numeric sorts: NaNs go to the end
	if (Number.isNaN(an) && Number.isNaN(bn)) return 0
	if (Number.isNaN(an)) return 1
	if (Number.isNaN(bn)) return -1
	return an - bn
}

export function buildSorter<T>(
	keys: Array<{
		type: SortKeyType
		get: (item: T) => string
	}>
): (a: T, b: T) => number {
	return (a: T, b: T) => {
		for (const key of keys) {
			const av = key.get(a)
			const bv = key.get(b)
			const cmp = key.type === 'numeric' ? compareNumeric(av, bv) : compareAlpha(av, bv)
			if (cmp !== 0) return cmp
		}
		return 0
	}
}

// ---- Specific sort comparators ----

// 1) Default alpha (whole label)
export const sortByLabelAlpha = buildSorter<{ label: string }>([{ type: 'alpha', get: o => o.label ?? '' }])

// 2) Recruiter: first name, then last name
function splitName(label: string) {
	const parts = normalizeSortText(label).split(' ').filter(Boolean)
	const first = parts[0] ?? ''
	const last = parts.slice(1).join(' ')
	return { first, last }
}
export const sortByFirstLast = buildSorter<{ label: string }>([
	{ type: 'alpha', get: o => splitName(o.label ?? '').first },
	{ type: 'alpha', get: o => splitName(o.label ?? '').last },
	{ type: 'alpha', get: o => o.label ?? '' }, // stable tie-breaker
])

// 3) Address: compare by alternating alpha/numeric chunks
type Token = { t: 'n'; v: number } | { t: 's'; v: string }

function tokenizeAddress(label: string): Token[] {
	const normalized = normalizeSortText(label)
	if (!normalized) return []
	const parts = normalized.split(/(\d+)/).filter(Boolean)
	return parts.map(p => {
		if (/^\d+$/.test(p)) return { t: 'n' as const, v: Number(p) }
		return { t: 's' as const, v: p.trim() }
	})
}

export function compareAddressLike(aLabel: string, bLabel: string): number {
	const aT = tokenizeAddress(aLabel)
	const bT = tokenizeAddress(bLabel)
	const len = Math.max(aT.length, bT.length)

	for (let i = 0; i < len; i++) {
		const result = compareTokens(aT[i], bT[i])
		if (result !== 0) return result
	}

	return compareAlpha(aLabel, bLabel)
}

function compareTokens(at: Token | undefined, bt: Token | undefined): number {
	if (!at && bt) return -1
	if (at && !bt) return 1
	if (!at || !bt) return 0

	if (at.t === 'n' && bt.t === 'n') {
		return at.v - bt.v
	}

	// numeric chunks come before string chunks
	if (at.t !== bt.t) return at.t === 'n' ? -1 : 1

	return String(at.v).localeCompare(String(bt.v), undefined, { sensitivity: 'base' })
}
